// Read-only adapter for the Customer App's production discovery contract.
// Deliberately contains no auth fallback, writes, orders, or stock mutations.
import { collection, getDocs, query, where } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../lib/firebase";

const text = (value) => String(value ?? "").trim();
const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Mirrors lib/main.dart::homeMainCategories in the Customer App. Firestore
// products are the source of truth for which subcategories actually exist.
export const customerCategoryContract = [
  ["grocery", "Grocery", "🛒"],
  ["vegetables", "Vegetables", "🥬"],
  ["fruits", "Fruits", "🍎"],
  ["food", "Food", "🍔"],
  ["gifts", "Gifts", "💐"],
  ["gifts-surprises", "Gifts & Surprises", "🎁", "Gifts"],
  ["cosmetics", "Cosmetics", "💄"],
  ["electronics", "Electronics", "📱"],
  ["home-service", "Home Service", "🏠"],
  ["parcel-delivery", "Parcel Delivery", "📦"],
].map(([id, name, icon, firestoreCategory = name]) => ({
  id, name, icon, firestoreCategory, enabled: true, imageUrl: "", subcategories: [],
}));

function storageUrl(value) {
  const raw = text(value);
  if (!raw.startsWith("gs://")) return raw;
  const path = raw.slice(5);
  const separator = path.indexOf("/");
  if (separator <= 0 || separator === path.length - 1) return "";
  const bucket = path.slice(0, separator);
  const objectPath = path.slice(separator + 1);
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
}

function imageValue(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return storageUrl(value.downloadURL || value.downloadUrl || value.url);
  }
  return storageUrl(value);
}

export function productImages(data = {}) {
  const values = [data.imageUrl, data.imageUrls, data.images, data.image, data.imageURL, data.image_url];
  return values.flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map(imageValue)
    .filter((value, index, list) => value && list.indexOf(value) === index);
}

export function normaliseProduct(id, data = {}) {
  const stock = number(data.stock, 999);
  const price = number(data.price ?? data.sellingPrice);
  const mrp = number(data.oldPrice ?? data.old_price ?? data.mrp ?? data.originalPrice ?? data.strikePrice ?? data.compareAtPrice);
  const explicitDiscount = number(data.discount ?? data.discountPercent);
  const computedDiscount = mrp > price && mrp > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const weight = text(data.weight || data.quantity || data.size);
  const unit = text(data.unit || data.measureUnit);
  const popularity = number(data.popularity) + number(data.orderCount) + number(data.ordersCount)
    + number(data.sold) * 8 + number(data.soldCount) * 8 + number(data.sales) * 8
    + number(data.rating) * 100 + number(data.reviewCount) + number(data.views);
  return {
    id,
    name: text(data.name || data.title || "Untitled product"),
    price,
    mrp,
    discount: explicitDiscount > 0 ? Math.round(explicitDiscount) : computedDiscount,
    category: text(data.category || "Other"),
    subcategory: text(data.subcategory),
    childCategory: text(data.childCategory),
    description: text(data.shortDescription || data.description),
    brand: text(data.brand),
    weight,
    unit,
    unitLabel: [weight, unit].filter(Boolean).join(" "),
    images: productImages(data),
    stock: Math.max(0, Math.trunc(stock)),
    isActive: data.isActive !== false,
    shopId: text(data.shopId || data.storeId),
    shopName: text(data.shopNameSnapshot || data.shopName),
    popularity,
  };
}

function normaliseBanner(id, data = {}) {
  return {
    id,
    imageUrl: imageValue(data.imageUrl || data.image),
    title: text(data.title || data.name),
    subtitle: text(data.subtitle || data.description),
    isActive: data.isActive === true,
    displayOrder: number(data.order ?? data.displayOrder),
  };
}

function normalisePublicShop(id, data = {}) {
  return {
    id,
    storeId: text(data.storeId || id),
    name: text(data.name || "QuickDrop Shop"),
    area: text(data.area),
    imageUrl: imageValue(data.imageUrl),
    isActive: data.isActive === true,
  };
}

function normaliseInventory(id, data = {}) {
  return {
    id,
    productId: text(data.productId || id),
    available: data.available === true,
    stock: Math.max(0, Math.trunc(number(data.stock))),
  };
}

function deriveCategories(products) {
  return customerCategoryContract.map((category) => {
    const categoryProducts = products.filter((product) => product.category.toLowerCase() === category.firestoreCategory.toLowerCase());
    const subcategories = [...new Set(categoryProducts.map((product) => product.subcategory).filter(Boolean))];
    const imageUrl = categoryProducts.find((product) => product.images.length)?.images[0] || "";
    return { ...category, subcategories, imageUrl };
  });
}

async function readCollection(name, normalise, firestoreQuery) {
  if (!isFirebaseConfigured || !db) throw new Error("Firebase is not configured for this website.");
  const snapshot = await getDocs(firestoreQuery || collection(db, name));
  return snapshot.docs.map((document) => normalise(document.id, document.data()));
}

async function readPublicInventory(shop) {
  const inventoryQuery = query(
    collection(db, "public_shops", shop.id, "inventory"),
    where("available", "==", true),
    where("stock", ">", 0),
  );
  const items = await readCollection("inventory", normaliseInventory, inventoryQuery);
  return [shop.id, items.filter((item) => item.available && item.stock > 0)];
}

export async function loadCustomerDiscovery() {
  const results = await Promise.allSettled([
    readCollection("products", normaliseProduct),
    readCollection("banners", normaliseBanner, query(collection(db, "banners"), where("isActive", "==", true))),
    readCollection("public_shops", normalisePublicShop, query(collection(db, "public_shops"), where("isActive", "==", true))),
  ]);
  const names = ["products", "banners", "shops"];
  const values = Object.fromEntries(names.map((name, index) => [name, results[index].status === "fulfilled" ? results[index].value : []]));
  const errors = results.flatMap((result, index) => result.status === "rejected" ? [`${names[index]}: ${result.reason?.message || "unavailable"}`] : []);

  values.products = values.products.filter((product) => product.isActive);
  values.banners = values.banners.filter((banner) => banner.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
  values.shops = values.shops.filter((shop) => shop.isActive);
  const categories = deriveCategories(values.products);

  const inventoryResults = await Promise.allSettled(values.shops.map(readPublicInventory));
  const inventoryByShop = Object.fromEntries(inventoryResults.flatMap((result) => result.status === "fulfilled" ? [result.value] : []));
  errors.push(...inventoryResults.flatMap((result, index) => result.status === "rejected" ? [`shop inventory ${values.shops[index]?.id}: ${result.reason?.message || "unavailable"}`] : []));
  const productById = new Map(values.products.map((product) => [product.id, product]));
  const shopProducts = values.shops.flatMap((shop) => (inventoryByShop[shop.id] || []).flatMap((item) => {
    const product = productById.get(item.productId);
    return product ? [{ ...product, shopId: shop.id, shopName: shop.name, stock: item.stock, isActive: item.available }] : [];
  }));

  return { ...values, categories, inventoryByShop, shopProducts, errors };
}
